import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, formatMoney } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { financeApi } from '@/services/finance.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { PostJournalEntryDialog } from './post-journal-entry-dialog';

export function JournalEntriesPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isPosting, setIsPosting] = useState(false);

  const query = useQuery({
    queryKey: ['finance', 'journal-entries'],
    queryFn: () => financeApi.listJournalEntries({ limit: 50 }),
  });

  const entries = query.data?.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('finance.journalEntry.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('finance.journalEntry.count', { count: entries.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>
        {can('finance.journal_entry.create') && (
          <Button variant="primary" onClick={() => setIsPosting(true)}>
            {t('finance.journalEntry.post')}
          </Button>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && entries.length === 0 && (
        <EmptyState title={t('finance.journalEntry.empty')} />
      )}

      {entries.map((entry) => (
        <div className="table-scroll" key={entry.id}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col" colSpan={3}>
                  <span dir="ltr">{formatDate(entry.entryDate, locale)}</span> — {entry.description}
                  {!entry.createdByUserId && (
                    <span className="cell-empty">
                      {' '}
                      · {t('finance.journalEntry.systemGenerated')}
                    </span>
                  )}
                </th>
              </tr>
              <tr>
                <th scope="col">{t('finance.journalEntry.account')}</th>
                <th scope="col">{t('finance.journalEntry.debit')}</th>
                <th scope="col">{t('finance.journalEntry.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.accountName}</td>
                  <td dir="ltr">
                    {line.debit > 0 ? (
                      formatMoney(line.debit, 'USD', locale)
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                  <td dir="ltr">
                    {line.credit > 0 ? (
                      formatMoney(line.credit, 'USD', locale)
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {isPosting && (
        <PostJournalEntryDialog
          onClose={() => setIsPosting(false)}
          onPosted={() => {
            setIsPosting(false);
            void queryClient.invalidateQueries({ queryKey: ['finance', 'journal-entries'] });
          }}
        />
      )}
    </div>
  );
}
